import { createRng, type Rng } from '../../../core/rng';
import { ROLES } from '../citizens/citizenActing';
import { CROWD, CROWD_TRADES, NEAREST_OF, STAYS } from './crowdConfig';
import { ORDERS, PLACE_KINDS, type BakedCrowd, type PlaceKind } from './crowdPlan';
import { KEEPS } from './seats';
import type { WalkNet } from './walkNet';

/** The residents, one entry each, and their outings filed one after another. */
export interface Residents {
  readonly count: number;
  /** Which door each lives behind: an index into the baked doors. */
  readonly door: Uint16Array;
  /** Its trade: an index into CROWD_TRADES. */
  readonly trade: Uint8Array;
  readonly speed: Float32Array;
  readonly lane: Float32Array;
  readonly scale: Float32Array;
  /** How early each rises, from 0, with the day, to 1, last (CROWD.riseSpread). */
  readonly rise: Float32Array;
  /** A resident's outings run from first[r] to first[r + 1]. */
  readonly first: Int32Array;
  readonly outings: Outings;
  /** Residents with nowhere to go on their own piece of the net: they stay in. */
  readonly housebound: number;
  /** Residents who keep a seat of their own: a pitch, a place at the benches, a place at work. */
  readonly keepers: number;
}

export interface Outings {
  readonly count: number;
  readonly place: Uint16Array;
  /** Where in its part of the day the outing starts, from 0, at once, to 1, as late as it fits. */
  readonly start: Float32Array;
  readonly stay: Float32Array;
  /** Where the figure stands at the place, from the place's point. */
  readonly standX: Float32Array;
  readonly standY: Float32Array;
  /** The way it faces there; NaN where it has no seat, and turns to the place's point. */
  readonly facing: Float32Array;
  /** What it does there (ROLES), and whose company it is in: its seat's, or -1. */
  readonly role: Uint8Array;
  readonly group: Int32Array;
  /** The way from the door to the place and the way home, in metres, each along the walker's own
   *  lane; NaN until the place's routes are found (crowdSim.ts). */
  readonly outM: Float32Array;
  readonly backM: Float32Array;
}

export interface Place {
  readonly kind: PlaceKind;
  readonly node: number;
  /** How far round its point a figure may stand. */
  readonly room: number;
}

export function placesOf(baked: BakedCrowd): readonly Place[] {
  const places: Place[] = [];
  for (let i = 0; i < baked.places.length; i += 3)
    places.push({
      kind: PLACE_KINDS[baked.places[i]!]!,
      node: baked.places[i + 1]!,
      room: baked.places[i + 2]!,
    });
  return places;
}

/** The baked seats, one entry each; `groups` is how many companies they make up. */
export interface Seats {
  readonly count: number;
  readonly place: Int32Array;
  readonly x: Float32Array;
  readonly y: Float32Array;
  readonly facing: Float32Array;
  readonly role: Uint8Array;
  readonly group: Int32Array;
  readonly groups: number;
}

const SEAT_NUMBERS = 6;

export function seatsOf(baked: BakedCrowd): Seats {
  const count = baked.seats.length / SEAT_NUMBERS;
  const column = <T extends Float32Array | Int32Array | Uint8Array>(made: T, at: number): T => {
    for (let s = 0; s < count; s++) made[s] = baked.seats[s * SEAT_NUMBERS + at]!;
    return made;
  };
  const group = column(new Int32Array(count), 5);
  return {
    count,
    place: column(new Int32Array(count), 0),
    x: column(new Float32Array(count), 1),
    y: column(new Float32Array(count), 2),
    facing: column(new Float32Array(count), 3),
    role: column(new Uint8Array(count), 4),
    group,
    groups: group.reduce((most, g) => Math.max(most, g + 1), 0),
  };
}

/* What a resident comes to as a buyer: a pitch or a bench with someone who keeps it. */
const BOUGHT_AT: ReadonlySet<PlaceKind> = new Set<PlaceKind>(['market', 'stall']);

type ByKind = Record<PlaceKind, number[]>;
const byKind = (): ByKind =>
  Object.fromEntries(PLACE_KINDS.map((kind) => [kind, [] as number[]])) as ByKind;

function pickTrade(rng: Rng): number {
  const total = CROWD_TRADES.reduce((sum, trade) => sum + trade.share, 0);
  let at = rng.next() * total;
  for (const [i, trade] of CROWD_TRADES.entries()) {
    at -= trade.share;
    if (at < 0) return i;
  }
  return CROWD_TRADES.length - 1;
}

/* A kind of place for an errand of a trade, among the kinds it goes to that there is one of. */
function pickKind(trade: number, has: (kind: PlaceKind) => boolean, rng: Rng): PlaceKind | null {
  const goes = CROWD_TRADES[trade]!.goes;
  const kinds = PLACE_KINDS.filter((kind) => (goes[kind] ?? 0) > 0 && has(kind));
  const total = kinds.reduce((sum, kind) => sum + goes[kind]!, 0);
  let at = rng.next() * total;
  for (const kind of kinds) {
    at -= goes[kind]!;
    if (at < 0) return kind;
  }
  return kinds[kinds.length - 1] ?? null;
}

interface Drafted {
  readonly place: number;
  readonly start: number;
  readonly stay: number;
  readonly x: number;
  readonly y: number;
  readonly facing: number;
  readonly role: number;
  readonly group: number;
}

/**
 * The residents and their days, drawn from the seed: who lives behind which door, what their
 * trade is, and where they go. It is the same city on every visit.
 *
 * Those whose trade is plied in the open take a seat of their own first, one resident to a seat:
 * a pitch on the market, a place at the fish benches, a place at a hull or a stack. Everyone's
 * other outings are errands, and a buyer goes only where someone sells.
 */
export function planResidents(
  baked: BakedCrowd,
  net: WalkNet,
  seed: number,
  count: number = CROWD.residents
): Residents {
  const rng = createRng(seed).fork('crowd');
  const places = placesOf(baked);
  const seats = seatsOf(baked);
  const doors = baked.doors.length / 5;
  const houseDoors = Math.min(baked.houseDoors, doors);
  const homes: number[][] = ORDERS.map(() => []);
  for (let i = 0; i < baked.cloisters.length; i += 2)
    homes[baked.cloisters[i + 1]!]!.push(baked.cloisters[i]!);
  /* Each place's seats: those kept by one resident, still to be taken, and those anyone takes. */
  const kept: number[][] = places.map(() => []),
    taken: number[][] = places.map(() => []);
  for (let s = 0; s < seats.count; s++)
    (KEEPS.has(ROLES[seats.role[s]!]!) ? kept : taken)[seats.place[s]!]!.push(s);
  /* The places of each kind on each piece of the net, and those of them with a seat to keep. */
  const byPiece = new Map<number, { all: ByKind; vacant: ByKind; staffed: ByKind }>();
  places.forEach((place, i) => {
    const piece = net.piece[place.node]!;
    let filed = byPiece.get(piece);
    if (!filed)
      byPiece.set(piece, (filed = { all: byKind(), vacant: byKind(), staffed: byKind() }));
    filed.all[place.kind].push(i);
    if (kept[i]!.length > 0) filed.vacant[place.kind].push(i);
  });
  const door = new Uint16Array(count),
    trade = new Uint8Array(count),
    speed = new Float32Array(count),
    lane = new Float32Array(count),
    scale = new Float32Array(count),
    rise = new Float32Array(count),
    first = new Int32Array(count + 1);
  const keeps = new Int32Array(count).fill(-1);
  const staffed = new Uint8Array(seats.groups);
  const turns = ORDERS.map(() => 0);
  const pieceOf = (r: number): number =>
    doors ? (net.piece[baked.doors[door[r]! * 5 + 4]!] ?? -1) : -1;
  let lay = 0,
    keepers = 0;
  for (let r = 0; r < count; r++) {
    const own = rng.fork(`resident-${String(r)}`);
    trade[r] = pickTrade(own);
    const { order, job } = CROWD_TRADES[trade[r]!]!;
    const house = order ? homes[ORDERS.indexOf(order)]! : [];
    // Every house in turn, so the residents spread evenly over the doors.
    if (house.length > 0) door[r] = house[turns[ORDERS.indexOf(order!)]!++ % house.length]!;
    else door[r] = houseDoors ? lay++ % houseDoors : 0;
    speed[r] = own.range(CROWD.speed[0], CROWD.speed[1]);
    lane[r] = own.range(CROWD.lane[0], CROWD.lane[1]);
    scale[r] = own.range(CROWD.scale[0], CROWD.scale[1]);
    rise[r] = own.next();
    const vacant = job ? byPiece.get(pieceOf(r))?.vacant[job.at] : undefined;
    if (!vacant || vacant.length === 0) continue;
    const pick = own.int(0, vacant.length - 1),
      place = vacant[pick]!;
    keeps[r] = kept[place]!.pop()!;
    staffed[seats.group[keeps[r]!]!] = 1;
    keepers++;
    if (kept[place]!.length === 0) {
      vacant[pick] = vacant[vacant.length - 1]!;
      vacant.pop();
    }
  }
  /* A buyer's seats at a place are those before someone who sells there. */
  const visited: number[][] = places.map((place, i) =>
    BOUGHT_AT.has(place.kind) ? taken[i]!.filter((s) => staffed[seats.group[s]!]) : taken[i]!
  );
  for (const filed of byPiece.values())
    for (const kind of PLACE_KINDS)
      filed.staffed[kind] = BOUGHT_AT.has(kind)
        ? filed.all[kind].filter((i) => visited[i]!.length > 0)
        : filed.all[kind];
  const next = new Int32Array(places.length);
  /* The nearest to a home of `tries` places drawn from those there are. */
  const nearest = (among: readonly number[], tries: number, home: number, own: Rng): number => {
    let chosen = -1,
      gap = Infinity;
    for (let n = 0; n < tries; n++) {
      const candidate = among[own.int(0, among.length - 1)]!;
      const node = places[candidate]!.node;
      const d = Math.hypot(net.x[node]! - net.x[home]!, net.y[node]! - net.y[home]!);
      if (d < gap) [gap, chosen] = [d, candidate];
    }
    return chosen;
  };
  const drafted: Drafted[] = [];
  let housebound = 0;
  for (let r = 0; r < count; r++) {
    const own = rng.fork(`resident-${String(r)}`).fork('day');
    first[r] = drafted.length;
    const local = byPiece.get(pieceOf(r))?.staffed;
    if (!local) {
      housebound++;
      continue;
    }
    const home = baked.doors[door[r]! * 5 + 4]!;
    const job = CROWD_TRADES[trade[r]!]!.job;
    const outings = own.int(CROWD.outings[0], CROWD.outings[1]);
    for (let o = 0; o < outings; o++) {
      let seat = -1,
        chosen: number,
        stay: number;
      if (keeps[r]! >= 0 && own.next() < job!.share) {
        seat = keeps[r]!;
        chosen = seats.place[seat]!;
        stay = own.range(CROWD.keepS[0], CROWD.keepS[1]);
      } else {
        const kind = pickKind(trade[r]!, (k) => local[k].length > 0, own);
        if (!kind) break;
        chosen = nearest(local[kind], NEAREST_OF[kind], home, own);
        const free = visited[chosen]!;
        if (free.length > 0) seat = free[next[chosen]!++ % free.length]!;
        stay = own.range(STAYS[kind].seconds[0], STAYS[kind].seconds[1]);
      }
      const reach = Math.min(CROWD.standM, places[chosen]!.room) * Math.sqrt(own.next());
      const turn = own.range(0, Math.PI * 2);
      drafted.push({
        place: chosen,
        start: own.next(),
        stay,
        x: seat >= 0 ? seats.x[seat]! : Math.cos(turn) * reach,
        y: seat >= 0 ? seats.y[seat]! : Math.sin(turn) * reach,
        facing: seat >= 0 ? seats.facing[seat]! : NaN,
        role: seat >= 0 ? seats.role[seat]! : 0,
        group: seat >= 0 ? seats.group[seat]! : -1,
      });
    }
  }
  first[count] = drafted.length;
  return {
    count,
    door,
    trade,
    speed,
    lane,
    scale,
    rise,
    first,
    housebound,
    keepers,
    outings: {
      count: drafted.length,
      place: Uint16Array.from(drafted, (o) => o.place),
      start: Float32Array.from(drafted, (o) => o.start),
      stay: Float32Array.from(drafted, (o) => o.stay),
      standX: Float32Array.from(drafted, (o) => o.x),
      standY: Float32Array.from(drafted, (o) => o.y),
      facing: Float32Array.from(drafted, (o) => o.facing),
      role: Uint8Array.from(drafted, (o) => o.role),
      group: Int32Array.from(drafted, (o) => o.group),
      outM: new Float32Array(drafted.length).fill(NaN),
      backM: new Float32Array(drafted.length).fill(NaN),
    },
  };
}
